import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { SectionTitle } from "@/components/GlassBits";
import { Mail, Send, MessageSquare, Volume2, VolumeX } from "lucide-react";
import { isSoundEnabled, setSoundEnabled, sounds } from "@/lib/sound";

export default function Settings() {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [soundOn, setSoundOn] = useState(isSoundEnabled());
  const [smtp, setSmtp] = useState({
    host: "", port: 587, username: "", password: "", sender_email: "", sender_name: "LostDRKC Payments", enabled: false, has_password: false,
  });
  const [discord, setDiscord] = useState({ webhook_url: "", enabled: false });

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings");
      setSmtp({ host: "", port: 587, username: "", sender_email: "", sender_name: "LostDRKC Payments", enabled: false, has_password: false, ...(data.smtp || {}), password: "" });
      setDiscord({ webhook_url: "", enabled: false, ...(data.discord || {}) });
    } catch (e) {
      console.error("[Settings] load failed", e);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const saveSmtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { smtp: { ...smtp } };
      // Do not send has_password
      delete payload.smtp.has_password;
      if (!payload.smtp.password) delete payload.smtp.password;
      await api.patch("/admin/settings", payload);
      toast.success("SMTP settings saved");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const saveDiscord = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch("/admin/settings", { discord });
      toast.success("Discord settings saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doTestEmail = async () => {
    if (!testTo) { toast.error("Enter a recipient"); return; }
    try {
      const { data } = await api.post("/admin/settings/test-email", { to: testTo });
      if (data.status === "sent") toast.success(`Sent to ${testTo}`);
      else toast.error(data.error || data.reason || "Failed");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const doTestDiscord = async () => {
    try {
      const { data } = await api.post("/admin/settings/test-discord");
      if (data.status === "sent") toast.success("Sent to Discord");
      else toast.error(data.error || "Failed");
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  if (loading) return <div className="text-white/50">Loading…</div>;

  return (
    <div>
      <SectionTitle eyebrow="Configuration" title="Settings" />

      <div className="grid lg:grid-cols-2 gap-4">
        {/* SMTP */}
        <form onSubmit={saveSmtp} className="ldc-glass p-6" data-testid="settings-smtp-form">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded bg-[#fbce18]/10 border border-[#fbce18]/30 flex items-center justify-center text-[#fbce18]"><Mail size={16}/></div>
            <div>
              <div className="font-display text-lg">Email · SMTP</div>
              <div className="text-xs text-white/40">Used for payment link + receipt emails</div>
            </div>
            <label className="ml-auto inline-flex items-center gap-2 text-xs text-white/60">
              <input type="checkbox" checked={smtp.enabled} onChange={(e) => setSmtp({...smtp, enabled: e.target.checked})} className="accent-[#fbce18]" data-testid="settings-smtp-enabled" />
              Enabled
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <F label="Host" v={smtp.host} on={(v) => setSmtp({...smtp, host: v})} testid="settings-smtp-host" placeholder="smtp.gmail.com" />
            <F label="Port" type="number" v={smtp.port} on={(v) => setSmtp({...smtp, port: parseInt(v)||587})} testid="settings-smtp-port" />
            <F label="Username" v={smtp.username} on={(v) => setSmtp({...smtp, username: v})} testid="settings-smtp-username" />
            <F label={smtp.has_password ? "Password (••• leave blank to keep)" : "Password"} type="password" v={smtp.password || ""} on={(v) => setSmtp({...smtp, password: v})} testid="settings-smtp-password" />
            <F label="Sender email" type="email" v={smtp.sender_email} on={(v) => setSmtp({...smtp, sender_email: v})} testid="settings-smtp-sender" />
            <F label="Sender name" v={smtp.sender_name} on={(v) => setSmtp({...smtp, sender_name: v})} testid="settings-smtp-sender-name" />
          </div>
          <div className="mt-5 flex flex-wrap gap-2 items-center">
            <button type="submit" disabled={busy} className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="settings-smtp-save">Save</button>
            <div className="flex-1 flex items-center gap-2 min-w-[220px]">
              <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="test@example.com" className="ldc-input flex-1 rounded-md px-3 py-2 text-sm" data-testid="settings-smtp-test-to" />
              <button type="button" onClick={doTestEmail} className="px-3 py-2 rounded-md border border-white/10 hover:border-[#fbce18]/40 text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2" data-testid="settings-smtp-test-btn">
                <Send size={12}/> Send test
              </button>
            </div>
          </div>
        </form>

        {/* Discord */}
        <form onSubmit={saveDiscord} className="ldc-glass p-6" data-testid="settings-discord-form">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-9 h-9 rounded bg-[#5865F2]/15 border border-[#5865F2]/40 flex items-center justify-center text-[#8b95ff]"><MessageSquare size={16}/></div>
            <div>
              <div className="font-display text-lg">Discord Webhook</div>
              <div className="text-xs text-white/40">Broadcast payments, refunds, link events</div>
            </div>
            <label className="ml-auto inline-flex items-center gap-2 text-xs text-white/60">
              <input type="checkbox" checked={discord.enabled} onChange={(e) => setDiscord({...discord, enabled: e.target.checked})} className="accent-[#fbce18]" data-testid="settings-discord-enabled" />
              Enabled
            </label>
          </div>
          <F label="Webhook URL" v={discord.webhook_url} on={(v) => setDiscord({...discord, webhook_url: v})} testid="settings-discord-url" placeholder="https://discord.com/api/webhooks/…" />
          <div className="mt-5 flex flex-wrap gap-2">
            <button type="submit" disabled={busy} className="ldc-btn-primary px-5 py-2 rounded-md text-sm" data-testid="settings-discord-save">Save</button>
            <button type="button" onClick={doTestDiscord} className="px-4 py-2 rounded-md border border-white/10 hover:border-[#fbce18]/40 text-xs uppercase tracking-[0.16em] inline-flex items-center gap-2" data-testid="settings-discord-test-btn">
              <Send size={12}/> Send test embed
            </button>
          </div>
        </form>

        {/* Sound toggle */}
        <div className="ldc-glass p-6 lg:col-span-2" data-testid="settings-sound-block">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#fbce18]/10 border border-[#fbce18]/30 flex items-center justify-center text-[#fbce18]">
              {soundOn ? <Volume2 size={16}/> : <VolumeX size={16}/>}
            </div>
            <div className="flex-1">
              <div className="font-display text-lg">Notification sounds</div>
              <div className="text-xs text-white/40">Play tones for new payments, refunds, and other events. Preference stored in this browser.</div>
            </div>
            <button
              type="button"
              onClick={() => { const v = !soundOn; setSoundOn(v); setSoundEnabled(v); if (v) sounds.info(); toast.success(v ? "Sounds enabled" : "Sounds disabled"); }}
              className={`px-5 py-2 rounded-md text-sm border transition-colors ${soundOn ? "bg-[#fbce18] text-black border-[#fbce18]" : "border-white/10 text-white/70 hover:border-[#fbce18]/40"}`}
              data-testid="settings-sound-toggle"
            >
              {soundOn ? "On" : "Off"}
            </button>
            <button type="button" onClick={() => sounds.payment()} className="px-3 py-2 rounded-md border border-white/10 text-xs uppercase tracking-[0.16em]" data-testid="settings-sound-preview">Preview</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", testid, placeholder }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.24em] uppercase text-white/40 mb-1.5">{label}</div>
      <input type={type} value={v ?? ""} onChange={(e) => on(e.target.value)} placeholder={placeholder} className="ldc-input w-full rounded-md px-3 py-2 text-sm" data-testid={testid} />
    </label>
  );
}
